<template>
  <div
    class="app-progress-wrap"
    :class="{ active: down, thin, rtl: reverse, noBg, continuous }"
  >
    <div v-if="!noLabels" class="page-number">
      <slot
        name="label-prepend"
        v-bind="{ max, min, modelValue, sliderProgress, loading }"
      >
        {{ loading ? "?" : (min ?? 0) }}
      </slot>
    </div>
    <div
      class="page-slider"
      :class="{
        continuous,
        rtl: reverse,
        disabled: disabled || loading,
        'mx-2': !noLabels,
      }"
      ref="pageSlider"
      @touchstart.prevent.stop="touchDown"
      @mousedown.prevent.stop="touchDown"
    >
      <button
        v-if="!loading"
        ref="sliderBall"
        id="slider-ball"
        :class="{
          continuous,
          rtl: reverse,
          highlight: shouldHighlightIndex(modelValue),
          [highlight?.color ?? '']: !!highlight,
        }"
        @touchstart.prevent.stop="touchDown"
        @mousedown.prevent.stop="touchDown"
      >
        <slot
          name="label-ball"
          v-if="!continuous && !hideNumber"
          v-bind="{ max, min, modelValue, sliderProgress, loading }"
        >
          {{ loading ? "?" : sliderNumber }}
        </slot>
        <div
          class="slider-ball-tooltip"
          :class="{
            highlight: shouldHighlightIndex(modelValue),
            [highlight?.color ?? '']: !!highlight,
          }"
          v-else
          v-show="down && !noTooltip"
        >
          <slot
            name="label-tooltip"
            v-bind="{ max, min, modelValue, sliderProgress, loading }"
          >
            {{
              loading
                ? "?"
                : continuous
                  ? sliderProgress.toFixed(2)
                  : sliderNumber
            }}
          </slot>
        </div>
      </button>
      <div
        v-if="flag"
        class="bg-red-500 h-[calc(100%+1.25rem)] absolute bottom-0 z-[5] rounded-b-full"
        :style="{
          left: flagOffset,
          width: `calc(2 * ${dividerMargin})`,
        }"
      >
        <div
          class="text-xs absolute bg-red-500 text-white font-bold px-1.5"
          :class="{
            'right-0 rounded-l-full':
              flag.direction ===
              (reverse ? 'small-to-large' : 'large-to-small'),
            'left-0 rounded-r-full':
              flag.direction ===
              (reverse ? 'large-to-small' : 'small-to-large'),
          }"
        >
          {{ flag.text }}
        </div>
      </div>
      <div
        v-if="
          !loading && (continuous ? max > (min ?? 0) : max - (min ?? 0) > 1)
        "
        class="slider-dividers"
        :class="{
          rtl: reverse,
          continuous,
          highlight: highlightGradient,
        }"
      >
        <template v-if="!continuous">
          <div
            v-for="divider in max - (min ?? 0) + 1"
            :class="{
              continuous,
              highlight: shouldHighlightIndex(divider),
              [highlight?.color ?? '']: !!highlight,
              disabled: disabledSteps?.includes(divider - 1),
            }"
            class="prog-divider"
          ></div>
        </template>
      </div>
      <div class="slider-loading" v-show="loading" />
    </div>
    <div class="page-number" v-if="!noLabels" :class="{}">
      <slot
        name="label-postpend"
        v-bind="{ max, min, modelValue, sliderProgress, loading }"
      >
        {{ loading ? "?" : max }}
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
interface Props {
  modelValue: number;
  min?: number;
  max: number;
  continuous?: boolean;
  hideNumber?: boolean;
  thin?: boolean;
  reverse?: boolean;
  disabled?: boolean;
  loading?: boolean;
  noLabels?: boolean;
  noTooltip?: boolean;
  noBg?: boolean;
  immediate?: boolean;
  disabledSteps?: number[];
  noDividerMargin?: boolean;
  highlight?:
    | {
        color: "danger" | "gold" | "accent";
        min?: number;
        max: number;
      }
    | {
        color: "danger" | "gold" | "accent";
        min: number;
        max?: number;
      };
  flag?: {
    text: string;
    index: number;
    direction: "small-to-large" | "large-to-small";
  };
}

interface Emits {
  (e: "update:modelValue", v: number): void;
  (e: "dragStart"): void;
  (e: "dragEnd"): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const pageSlider = ref<HTMLDivElement | null>(null);
const sliderBall = ref<HTMLDivElement | null>(null);

const shouldHighlightIndex = (index: number) => {
  if (!props.highlight) return false;
  const normalized = index - (props.min ?? 0);

  if (props.highlight.max === undefined && normalized >= props.highlight.min!)
    return true;
  if (props.highlight.min === undefined && normalized <= props.highlight.max!)
    return true;
  if (normalized >= props.highlight.min! && index <= props.highlight.max!)
    return true;
};

const highlightGradient = computed(() => {
  if (!props.highlight) return;

  const start = props.highlight?.min ?? 0;
  const end = props.highlight?.max ?? props.max;
  const divisions = props.max - (props.min ?? 0) + 1;

  // TODO: do something about these colors
  const nami = "rgba(2,195,255)";
  let color;

  switch (props.highlight.color) {
    case "danger":
      color = "rgb(248,113,113)";
      break;
    case "gold":
      color = "rgb(253,224,71)";
      break;
    // dont apply any color
    case "accent":
    default:
      return;
  }

  return `linear-gradient(to ${props.reverse ? "left" : "right"}, ${nami} ${
    (start / divisions) * 100
  }%, ${color} ${(start / divisions) * 100}%, ${color} ${
    (end / divisions) * 100
  }%, ${nami} ${(end / divisions) * 100}%)`;
});

const flagOffset = computed(() => {
  if (!props.flag) return undefined;

  const normalized = props.flag.index - (props.min ?? 0);
  const total = props.max - (props.min ?? 0) + 1;
  let percent = normalized / total;

  if (props.reverse) percent = 1 - percent;

  return `calc(${percent * 100}% - ${dividerMargin.value})`;
});

const down = ref(false);
const sliderProgress = computed(() => {
  if (props.continuous) return props.modelValue;
  else
    return (
      (props.modelValue - (props.min ?? 0)) /
      Math.max(1, props.max - (props.min ?? 0))
    );
});
const internalProgress = ref(
  props.continuous
    ? props.modelValue
    : (props.modelValue - (props.min ?? 0)) /
        Math.max(1, props.max - (props.min ?? 0)),
);

const ballWidth = computed(() => {
  const percentWidth = 100 / Math.max(1, props.max - (props.min ?? 0) + 1);
  return `${percentWidth}%`;
});
const dividerMargin = computed(() => {
  if (props.noDividerMargin) return "0px";

  const slider = pageSlider.value;
  if (!slider) return;
  const sliderBox = slider.getBoundingClientRect();
  const divWidth = clamp(
    (sliderBox.width - props.max - (props.min ?? 0) + 1) /
      (props.max - (props.min ?? 0) + 1),
    1,
    0,
  );
  return Math.floor(divWidth) + "px";
});
const offsetStyle = computed(() => {
  // how much of the slider is actually used
  let trackPercent = `(100% - ${ballWidth.value})`;
  let trackOffset = `(${ballWidth.value} / 2)`;
  // raw percent based on location on slider
  let progress = sliderProgress.value;
  if (props.continuous) {
    progress = (progress - (props.min ?? 0)) / (props.max - (props.min ?? 0));
    trackPercent = "calc(100% - 1rem)";
    trackOffset = "0.5rem";
  } else
    progress =
      Math.round(progress * Math.max(0, props.max - (props.min ?? 0))) /
      Math.max(1, props.max - (props.min ?? 0));
  // remove width of ball from slider
  return `calc(${progress} * ${trackPercent} + ${trackOffset})`;
});
const sliderNumber = computed(
  () =>
    Math.round(internalProgress.value * (props.max - (props.min ?? 0))) +
    (props.min ?? 0),
);

watch(sliderNumber, (v) => {
  if (sliderNumber.value === props.modelValue) return;
  if (!props.continuous && !props.disabledSteps?.includes(v)) {
    emit("update:modelValue", v);
  }
});
watch(
  () => props.modelValue,
  (v) => {
    if (props.continuous) internalProgress.value = v;
    else
      internalProgress.value =
        (v - (props.min ?? 0)) / Math.max(1, props.max - (props.min ?? 0));
  },
);

const isMouseEvent = (e: TouchEvent | MouseEvent): e is MouseEvent =>
  !(e as TouchEvent).touches;
const clamp = (num: number, upper: number, lower: number) =>
  Math.max(lower, Math.min(num, upper));

const touchDown = (e: TouchEvent | MouseEvent) => {
  down.value = true;
  emit("dragStart");
  calculateSliderProgress(e);
  if (isMouseEvent(e)) {
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  } else {
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
  }
};
const move = (e: TouchEvent | MouseEvent) => {
  calculateSliderProgress(e);
  let touchY;
  if (isMouseEvent(e)) {
    touchY = e.clientY;
  } else {
    touchY = e.touches[0].clientY;
  }

  const touchHeightRatio = touchY / window.innerHeight;
};
const up = () => {
  down.value = false;
  emit("dragEnd");
  window.removeEventListener("touchmove", move);
  window.removeEventListener("touchend", up);
  window.removeEventListener("mousemove", move);
  window.removeEventListener("mouseup", up);
};
const calculateSliderProgress = (e: TouchEvent | MouseEvent) => {
  let touchX;
  if (isMouseEvent(e)) {
    touchX = e.clientX;
  } else {
    touchX = e.touches[0].clientX;
  }

  const slider = pageSlider.value;
  if (!slider) return;
  const sliderBox = slider.getBoundingClientRect();

  // reference start of slider box, account for ball width
  let normalizedTouchX =
    touchX -
    sliderBox.left -
    sliderBox.width / (props.max - (props.min ?? 0) + 1) / 2;
  let adjustedSliderWidth =
    sliderBox.width - sliderBox.width / (props.max - (props.min ?? 0) + 1);
  if (props.continuous) {
    const ballWidth = sliderBall.value?.getBoundingClientRect().width ?? 16;
    normalizedTouchX = touchX - sliderBox.left - ballWidth / 2;
    adjustedSliderWidth = sliderBox.width - ballWidth;
  }

  const calculatedProgress = normalizedTouchX / adjustedSliderWidth;
  if (props.reverse)
    internalProgress.value = clamp(1 - calculatedProgress, 1, 0);
  else internalProgress.value = clamp(calculatedProgress, 1, 0);
  if (props.continuous)
    emit(
      "update:modelValue",
      internalProgress.value * (props.max - (props.min ?? 0)) +
        (props.min ?? 0),
    );
  else if (props.immediate) emit("update:modelValue", sliderNumber.value);
};
</script>

<style scoped lang="postcss">
.app-progress-wrap {
  --slider-transition: 75ms ease-in-out;
  --slider-height: 1rem;
  --slider-ball-diameter: 1rem;
  --progress-transition: 75ms ease-in-out;
  height: 2.5rem;
  transition: all var(--progress-transition);
  user-select: none;
  @apply flex items-center px-4 rounded-full;

  &.continuous {
    --progress-transition: 0ms ease-in-out;
  }

  &:not(.noBg) {
    @apply bg-white dark:bg-black;
  }

  &.rtl {
    flex-direction: row-reverse;
  }

  &.active {
    transform: none;

    .slider-ball-tooltip {
      display: block;
    }
  }

  &.thin {
    --slider-height: 0.5rem;
  }

  &.thick {
    --slider-height: 2rem;
  }
}

.page-number {
  min-width: 0;
  transition:
    min-width var(--progress-transition),
    max-width var(--progress-transition);
  @apply text-xs whitespace-nowrap flex justify-center;
}

.page-slider {
  height: var(--slider-height);
  transition:
    height var(--progress-transition),
    opacity var(--progress-transition);
  min-width: 0;
  @apply flex-grow bg-neutral-100 dark:bg-neutral-800 relative rounded-full cursor-pointer;
}

.slider-dividers {
  &:before {
    content: "";
    z-index: 1;
    @apply absolute bg-nami-nami-blue inset-0 opacity-50;
    transition: clip-path var(--progress-transition);
  }

  &.highlight:before {
    background: v-bind(highlightGradient) !important;
  }

  &:not(.continuous) > #slider-progress {
    transition: width var(--slider-transition);
  }

  &:not(.rtl) {
    &:before {
      clip-path: rect(0 v-bind(offsetStyle) 100% 0);
    }

    & > :first-child {
      margin-left: 0;
      padding-left: v-bind(dividerMargin);
    }

    & > :last-child {
      margin-right: 0;
      padding-right: v-bind(dividerMargin);
    }
  }

  &.rtl {
    &:before {
      clip-path: rect(0 100% 100% calc(100% - v-bind(offsetStyle)));
    }

    flex-direction: row-reverse;

    & > :first-child {
      margin-right: 0;
      padding-right: v-bind(dividerMargin);
    }

    & > :last-child {
      margin-left: 0;
      padding-left: v-bind(dividerMargin);
    }

    & > #slider-progress {
      left: unset;
      right: 0;
    }
  }

  @apply relative flex justify-evenly rounded-full overflow-hidden h-full;
}

.prog-divider {
  height: 100%;
  margin: 0 v-bind(dividerMargin);
  @apply flex-grow bg-neutral-200 dark:bg-neutral-700;

  &.continuous {
    margin: 0;
    transition: background-color var(--slider-transition);
  }

  &.highlight {
    &.accent {
      @apply bg-nami-comi-blue/20;
    }

    &.gold {
      @apply bg-yellow-300/20;
    }

    &.danger {
      @apply bg-red-400/20;
    }
  }

  &.disabled {
    @apply bg-neutral-50 dark:bg-neutral-900;
  }
}

#slider-ball {
  width: v-bind(ballWidth);
  height: var(--slider-height);
  min-width: var(--slider-ball-diameter);
  min-height: var(--slider-ball-diameter);
  top: 50%;
  left: v-bind(offsetStyle);
  transform: translateY(-50%) translateX(-50%);
  transition:
    min-width var(--progress-transition),
    width var(--progress-transition),
    height var(--progress-transition);
  z-index: 3;
  @apply rounded-full bg-nami-nami-blue absolute flex items-center justify-center text-white text-sm cursor-grab;

  &:active {
    &:after {
      @apply cursor-grabbing;
    }

    @apply cursor-grabbing;
  }

  &.continuous {
    max-width: var(--slider-ball-diameter);
    height: var(--slider-ball-diameter);
  }

  &.rtl {
    left: unset;
    right: v-bind(offsetStyle);
    transform: translateY(-50%) translateX(50%);
  }

  &:not(.continuous) {
    transition:
      left var(--slider-transition),
      right var(--slider-transition),
      min-width var(--progress-transition),
      height var(--progress-transition);
  }

  &:after {
    content: " ";
    height: calc(100% + 1rem);
    width: calc(100% + 1rem);
    @apply absolute cursor-grab;
  }

  &.highlight {
    &.accent {
      @apply bg-nami-comi-blue;
    }

    &.gold {
      @apply bg-yellow-300;
    }

    &.danger {
      @apply bg-red-400;
    }
  }
}

.slider-ball-tooltip {
  position: absolute;
  transform: translateY(-100%);
  @apply px-2 py-1 rounded-full text-lg leading-none bg-nami-nami-blue align-middle;

  &.highlight {
    &.accent {
      @apply bg-nami-comi-blue;
    }

    &.gold {
      @apply bg-yellow-300;
    }

    &.danger {
      @apply bg-red-400;
    }
  }
}

.slider-loading {
  @apply relative h-full overflow-hidden rounded-full bg-neutral-300 dark:bg-neutral-600;

  &:after {
    content: " ";
    animation-duration: 1s;
    animation-fill-mode: forwards;
    animation-iteration-count: infinite;
    animation-name: shimmer;
    animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    @apply absolute w-full h-full left-0 top-0 dark:filter dark:invert;
    background: linear-gradient(
      to right,
      rgba(80, 80, 80, 0) 0%,
      rgba(80, 80, 80, 1) 50%,
      rgba(80, 80, 80, 0) 100%
    );
  }
}

.thin .slider-loading {
  @apply -top-2;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}
</style>
